// form for creating order of payment - multi step form
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Calendar } from "@/components/ui/calendar";
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
   Select,
   SelectContent,
   SelectItem,
   SelectTrigger,
   SelectValue,
} from "@/components/ui/select";
import "@/components/ui/styles/customScrollBar.css";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { cn } from "@/lib/utils";
import { hasRole } from '@/utils/auth';
import { format } from "date-fns";
import { AlertCircle, ArrowLeft, CalendarIcon, ChevronDown, Loader2, MinusIcon, PlusIcon, UploadIcon, X } from "lucide-react";
import { toast } from 'sonner';
import { useOrderOfPayments } from '../hooks/useOrderOfPayments';



// Add DAO options constant
const DAO_OPTIONS = [
   {
      id: 'dao-2020-06',
      value: 'DAO 2020-06',
      label: 'DAO 2020-06',
      description: 'Expands the scope of DAO 2018-16'
   },
   {
      id: 'dao-2018-16',
      value: 'DAO 2018-16',
      label: 'DAO 2018-16',
      description: 'National Government Agency'
   },
   {
      id: 'dao-2018-09',
      value: 'DAO 2018-09',
      label: 'DAO 2018-09',
      description: 'Amendment of DAO 2003-24 for small scale wood processors'
   },
   {
      id: 'dao-2003-24',
      value: 'DAO 2003-24',
      label: 'DAO 2003-24',
      description: 'Chainsaw Registration'
   },
   {
      id: 'dao-2004-52',
      value: 'DAO 2004-52',
      label: 'DAO 2004-52',
      description: 'Issuance of cutting harvesting permits in private land'
   },
   {
      id: 'dao-2004-04',
      value: 'DAO 2004-04',
      label: 'DAO 2004-04',
      description: 'Guideline in utilization and transport of planted trees in private land'
   }
];

const CustomSelect = ({ options, onSelect, placeholder }) => {
   const [isOpen, setIsOpen] = useState(false);
   const [selected, setSelected] = useState(null);

   const handleSelect = (option) => {
      setSelected(option);
      onSelect(option.id);
      setIsOpen(false);
   };

   return (
      <div className="relative">
         <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className={cn(
               "flex h-10 w-full items-center justify-between rounded-md border border-input",
               "bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground",
               "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
               "disabled:cursor-not-allowed disabled:opacity-50"
            )}
         >
            <span>{selected ? `${selected.applicationNumber} - ${selected.ownerName || selected.name}` : placeholder}</span>
            <ChevronDown className="h-4 w-4 opacity-50" />
         </button>

         {isOpen && (
            <div className="absolute z-50 w-full mt-1 bg-background rounded-md shadow-lg border">
               <div className="py-1 max-h-60 overflow-auto">
                  {options.map((option) => (
                     <button
                        key={option.id}
                        type="button"
                        className={cn(
                           "w-full px-3 py-2 text-left text-sm hover:bg-accent",
                           "text-foreground",
                           selected?.id === option.id && "bg-accent"
                        )}
                        onClick={() => handleSelect(option)}
                     >
                        {option.applicationNumber} - {option.ownerName || option.name}
                     </button>
                  ))}
               </div>
            </div>
         )}
      </div>
   );
};

const OrderOfPaymentForm = ({ onClose, onSubmitStart, onSubmitEnd }) => {
   const navigate = useNavigate();
   const isMobile = useMediaQuery('(max-width: 640px)');

   const {
      applications,
      applicationsLoading,
      applicationsError,
      createOOP,
      updateSignature
   } = useOrderOfPayments();

   const [step, setStep] = useState(1);
   const [formData, setFormData] = useState({
      applicationId: '',
      applicationNumber: '',
      applicantName: '',
      billNo: '',
      date: new Date(),
      namePayee: '',
      address: '',
      natureOfApplication: '',
      fees: [{ id: 1, legalBasis: '', description: '', amount: '' }],
      rpsSignature: null,
      tsdSignature: null
   });

   // Add validation state
   const [validationErrors, setValidationErrors] = useState({});
   const firstInvalidFieldRef = useRef(null);

   const rpsFileInputRef = useRef(null);
   const tsdFileInputRef = useRef(null);

   const isChiefTSD = hasRole('Chief_TSD');
   const isChiefRPS = hasRole('Chief_RPS');

   const [isSubmitting, setIsSubmitting] = useState(false);

   const handleApplicationSelect = (applicationId) => {
      const selectedApp = applications.find(app => app.id === applicationId);
      if (selectedApp) {
         console.log('Selected application:', selectedApp);
         setFormData(prev => ({
            ...prev,
            applicationId: selectedApp.id,
            applicationNumber: selectedApp.applicationNumber,
            applicantName: selectedApp.ownerName || selectedApp.name,
            namePayee: selectedApp.ownerName || selectedApp.name,
            address: selectedApp.address,
            natureOfApplication: selectedApp.applicationType
         }));

         // Clear validation errors when selecting an application
         setValidationErrors(prev => ({
            ...prev,
            applicationId: undefined
         }));
      }
   };

   const handleInputChange = (e) => {
      const { name, value } = e.target;
      setFormData(prev => ({ ...prev, [name]: value }));

      // Clear validation error for this field
      if (validationErrors[name]) {
         setValidationErrors(prev => ({
            ...prev,
            [name]: undefined
         }));
      }
   };

   const handleFeeChange = (id, field, value) => {
      setFormData(prev => ({
         ...prev,
         fees: prev.fees.map(fee => {
            if (fee.id === id) {
               if (field === 'legalBasis') {
                  const selectedDAO = DAO_OPTIONS.find(dao => dao.value === value);
                  return {
                     ...fee,
                     legalBasis: value,
                     description: selectedDAO ? selectedDAO.description : fee.description
                  };
               }
               return { ...fee, [field]: value };
            }
            return fee;
         })
      }));

      // Clear validation errors for fees when they change
      setValidationErrors(prev => ({
         ...prev,
         fees: undefined
      }));
   };

   const addFeeRow = () => {
      setFormData(prev => ({
         ...prev,
         fees: [...prev.fees, { id: Date.now(), legalBasis: '', description: '', amount: '' }]
      }));
   };

   const removeFeeRow = (id) => {
      setFormData(prev => ({
         ...prev,
         fees: prev.fees.filter(fee => fee.id !== id)
      }));
   };

   const handleSignatureUpload = (event, signatureType) => {
      const file = event.target.files[0];
      if (file) {
         const reader = new FileReader();
         reader.onload = (e) => {
            setFormData(prev => ({ ...prev, [signatureType]: e.target.result }));

            // Clear validation error for this signature
            if (validationErrors[signatureType]) {
               setValidationErrors(prev => ({
                  ...prev,
                  [signatureType]: undefined
               }));
            }
         };
         reader.readAsDataURL(file);
      }
   };

   const triggerFileInput = (e, inputRef) => {
      e.preventDefault();
      e.stopPropagation();
      inputRef.current.click();
   };

   const handleDateChange = (date, field) => {
      setFormData(prev => ({ ...prev, [field]: date }));
   };

   const handleTimeChange = (e, field) => {
      setFormData(prev => ({
         ...prev,
         [field]: e.target.value
      }));
   };

   const validateStep = (stepNumber) => {
      const errors = {};

      if (stepNumber === 1) {
         if (!formData.applicationId) {
            errors.applicationId = 'Please select an application';
         }
      } else if (stepNumber === 2) {
         if (!formData.namePayee?.trim()) {
            errors.namePayee = 'Name/Payee is required';
         }
         if (!formData.address?.trim()) {
            errors.address = 'Address is required';
         }
         if (!formData.natureOfApplication?.trim()) {
            errors.natureOfApplication = 'Nature of Application is required';
         }

         // Validate fees
         const validFees = formData.fees.filter(fee =>
            fee.legalBasis && fee.description && fee.amount && !isNaN(parseFloat(fee.amount))
         );

         if (validFees.length === 0) {
            errors.fees = 'Please add at least one valid fee';
         }

         // Validate signatures based on user role
         if (isChiefRPS && !formData.rpsSignature) {
            errors.rpsSignature = 'Chief RPS signature is required';
         }

         if (isChiefTSD && !formData.tsdSignature) {
            errors.tsdSignature = 'Chief TSD signature is required';
         }
      }

      return errors;
   };

   const handleNext = () => {
      const errors = validateStep(step);
      setValidationErrors(errors);

      // If there are errors, focus on the first invalid field and return
      if (Object.keys(errors).length > 0) {
         // Use setTimeout to ensure DOM is updated
         setTimeout(() => {
            const firstErrorField = document.querySelector('[data-error="true"]');
            if (firstErrorField) {
               firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
               firstErrorField.focus();
            }
         }, 100);
         return;
      }

      setStep(step + 1);
   };

   const handlePrevious = () => {
      setStep(step - 1);
   };

   const handleSubmit = async (e) => {
      e.preventDefault();
      try {
         const errors = validateStep(step);
         setValidationErrors(errors);

         if (Object.keys(errors).length > 0) {
            // Use setTimeout to ensure DOM is updated
            setTimeout(() => {
               const firstErrorField = document.querySelector('[data-error="true"]');
               if (firstErrorField) {
                  firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  firstErrorField.focus();
               }
            }, 100);
            return;
         }

         setIsSubmitting(true);
         onSubmitStart?.();

         // Transform the data
         const transformedData = {
            applicationId: formData.applicationId.toString(),
            applicationNumber: formData.applicationNumber,
            namePayee: formData.namePayee.trim(),
            address: formData.address.trim(),
            natureOfApplication: formData.natureOfApplication.trim(),
            items: formData.fees
               .filter(fee => fee.legalBasis && fee.description && fee.amount && !isNaN(parseFloat(fee.amount)))
               .map(fee => ({
                  legalBasis: fee.legalBasis.trim(),
                  description: fee.description.trim(),
                  amount: parseFloat(fee.amount)
               }))
         };

         // Add signatures if they exist
         if (formData.rpsSignature) {
            transformedData.rpsSignatureImage = formData.rpsSignature;
         }
         if (formData.tsdSignature) {
            transformedData.tsdSignatureImage = formData.tsdSignature;
         }

         console.log('Submitting OOP data:', transformedData);

         const result = await createOOP(transformedData);

         if (result) {
            toast.success('Order of Payment created successfully');
            navigate('/personnel/dashboard');
         }
      } catch (error) {
         console.error('Error creating Order of Payment:', error);
         toast.error(`Failed to create Order of Payment: ${error.message}`);
      } finally {
         setIsSubmitting(false);
         onSubmitEnd?.();
      }
   };

   const handleBack = () => {
      navigate('/personnel/dashboard');
   };

   useEffect(() => {
      console.log('Applications in form:', applications);
   }, [applications]);

   const handleRemoveSignature = (signatureType, inputRef) => {
      // Clear the signature from formData
      setFormData(prev => ({ ...prev, [signatureType]: null }));

      // Reset the file input
      if (inputRef.current) {
         inputRef.current.value = '';
      }
   };

   const renderStep = () => {
      switch (step) {
         case 1:
            return (
               <div className="space-y-4">
                  <Label htmlFor="applicationSelect">Select Application</Label>
                  <div className="text-sm text-gray-500 mb-2">
                     Available applications: {applications?.length || 0}
                  </div>

                  {applicationsLoading ? (
                     <div className="h-10 flex items-center px-3 border rounded-md bg-gray-50">
                        Loading...
                     </div>
                  ) : (
                     <div
                        className={cn(
                           validationErrors.applicationId &&
                           "border-2 border-red-500 rounded-md p-1"
                        )}
                        data-error={validationErrors.applicationId ? "true" : "false"}
                     >
                        <CustomSelect
                           options={applications}
                           onSelect={handleApplicationSelect}
                           placeholder="Select an application"
                        />
                        {validationErrors.applicationId && (
                           <div className="flex items-center mt-1 text-sm text-red-500">
                              <AlertCircle className="h-4 w-4 mr-1" />
                              {validationErrors.applicationId}
                           </div>
                        )}
                     </div>
                  )}

                  <div className="space-y-4">
                     <div>
                        <Label htmlFor="applicationNumber">Application Number</Label>
                        <Input
                           id="applicationNumber"
                           name="applicationNumber"
                           value={formData.applicationNumber}
                           onChange={handleInputChange}
                           disabled
                           className="bg-muted dark:bg-gray-800"
                        />
                     </div>
                     <div>
                        <Label htmlFor="applicationId">Application ID</Label>
                        <Input
                           id="applicationId"
                           name="applicationId"
                           value={formData.applicationId}
                           onChange={handleInputChange}
                           disabled
                           className="bg-muted dark:bg-gray-800"
                        />
                     </div>
                     <div>
                        <Label htmlFor="applicantName">Applicant Name</Label>
                        <Input
                           id="applicantName"
                           name="applicantName"
                           value={formData.applicantName}
                           onChange={handleInputChange}
                           disabled
                           className="bg-muted dark:bg-gray-800"
                        />
                     </div>
                  </div>
               </div>
            );
         case 2:
            return (
               <div className="space-y-6 h-[630px] flex flex-col">
                  <div className="flex-grow overflow-auto custom-scrollbar pr-4">
                     <div className="space-y-6">
                        <div className="grid grid-cols-2 gap-4">
                           <div>
                              <Label htmlFor="billNo">Bill No.</Label>
                              <div className="h-10 flex items-center px-3 border rounded-md bg-muted dark:bg-gray-800">
                                 Auto-generated
                              </div>
                           </div>
                           <div>
                              <Label>Date</Label>
                              <Popover>
                                 <PopoverTrigger asChild>
                                    <Button variant="outline" className="w-full justify-start text-left">
                                       <CalendarIcon className="mr-2 h-4 w-4" />
                                       {format(formData.date, "MMM d, yyyy")}
                                    </Button>
                                 </PopoverTrigger>
                                 <PopoverContent className="w-auto p-0">
                                    <Calendar
                                       mode="single"
                                       selected={formData.date}
                                       onSelect={(date) => setFormData(prev => ({ ...prev, date }))}
                                       initialFocus
                                    />
                                 </PopoverContent>
                              </Popover>
                           </div>
                        </div>

                        <div>
                           <Label htmlFor="namePayee">Name/Payee</Label>
                           <div
                              className={cn(
                                 validationErrors.namePayee &&
                                 "border-2 border-red-500 rounded-md p-1"
                              )}
                              data-error={validationErrors.namePayee ? "true" : "false"}
                           >
                              <Input
                                 id="namePayee"
                                 name="namePayee"
                                 value={formData.namePayee}
                                 onChange={handleInputChange}
                              />
                              {validationErrors.namePayee && (
                                 <div className="flex items-center mt-1 text-sm text-red-500">
                                    <AlertCircle className="h-4 w-4 mr-1" />
                                    {validationErrors.namePayee}
                                 </div>
                              )}
                           </div>
                        </div>

                        <div>
                           <Label htmlFor="address">Address</Label>
                           <div
                              className={cn(
                                 validationErrors.address &&
                                 "border-2 border-red-500 rounded-md p-1"
                              )}
                              data-error={validationErrors.address ? "true" : "false"}
                           >
                              <Input
                                 id="address"
                                 name="address"
                                 value={formData.address}
                                 onChange={handleInputChange}
                              />
                              {validationErrors.address && (
                                 <div className="flex items-center mt-1 text-sm text-red-500">
                                    <AlertCircle className="h-4 w-4 mr-1" />
                                    {validationErrors.address}
                                 </div>
                              )}
                           </div>
                        </div>

                        <div>
                           <Label htmlFor="natureOfApplication">Nature of Application</Label>
                           <div
                              className={cn(
                                 validationErrors.natureOfApplication &&
                                 "border-2 border-red-500 rounded-md p-1"
                              )}
                              data-error={validationErrors.natureOfApplication ? "true" : "false"}
                           >
                              <Input
                                 id="natureOfApplication"
                                 name="natureOfApplication"
                                 value={formData.natureOfApplication}
                                 onChange={handleInputChange}
                              />
                              {validationErrors.natureOfApplication && (
                                 <div className="flex items-center mt-1 text-sm text-red-500">
                                    <AlertCircle className="h-4 w-4 mr-1" />
                                    {validationErrors.natureOfApplication}
                                 </div>
                              )}
                           </div>
                        </div>

                        <div>
                           <Label>Fees and Charges</Label>
                           <div
                              className={cn(
                                 validationErrors.fees &&
                                 "border-2 border-red-500 rounded-md p-1"
                              )}
                              data-error={validationErrors.fees ? "true" : "false"}
                           >
                              <Table>
                                 <TableHeader>
                                    <TableRow>
                                       <TableHead className="text-foreground">Legal Basis</TableHead>
                                       <TableHead className="text-foreground">Description</TableHead>
                                       <TableHead className="text-foreground">Amount</TableHead>
                                       <TableHead className="w-[50px]"></TableHead>
                                    </TableRow>
                                 </TableHeader>
                                 <TableBody>
                                    {formData.fees.map((fee, index) => (
                                       <TableRow key={fee.id}>
                                          <TableCell>
                                             <Select
                                                value={fee.legalBasis}
                                                onValueChange={(value) => handleFeeChange(fee.id, 'legalBasis', value)}
                                             >
                                                <SelectTrigger className="w-full">
                                                   <SelectValue placeholder="Select DAO" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                   {DAO_OPTIONS.map((dao) => (
                                                      <SelectItem key={dao.id} value={dao.value}>
                                                         {dao.label}
                                                      </SelectItem>
                                                   ))}
                                                </SelectContent>
                                             </Select>
                                          </TableCell>
                                          <TableCell>
                                             <Textarea
                                                value={fee.description}
                                                onChange={(e) => handleFeeChange(fee.id, 'description', e.target.value)}
                                                placeholder="Description of fee"
                                                disabled
                                                className="bg-muted resize-none h-20"
                                             />
                                          </TableCell>
                                          <TableCell>
                                             <Input
                                                type="number"
                                                value={fee.amount}
                                                onChange={(e) => handleFeeChange(fee.id, 'amount', e.target.value)}
                                                placeholder="0.00"
                                             />
                                          </TableCell>
                                          <TableCell>
                                             {index > 0 && (
                                                <Button
                                                   variant="ghost"
                                                   size="icon"
                                                   onClick={() => removeFeeRow(fee.id)}
                                                   className="text-red-500 hover:text-red-700"
                                                >
                                                   <MinusIcon className="h-4 w-4" />
                                                </Button>
                                             )}
                                          </TableCell>
                                       </TableRow>
                                    ))}
                                 </TableBody>
                              </Table>
                              {validationErrors.fees && (
                                 <div className="flex items-center mt-1 text-sm text-red-500">
                                    <AlertCircle className="h-4 w-4 mr-1" />
                                    {validationErrors.fees}
                                 </div>
                              )}
                           </div>

                           <div className="flex justify-between items-center mt-4">
                              <Button variant="outline" onClick={addFeeRow} type="button">
                                 <PlusIcon className="h-4 w-4 mr-2" /> Add Fee
                              </Button>
                              <div className="flex items-center">
                                 <Label className="mr-2">Total:</Label>
                                 <div className="w-32 px-3 py-2 border rounded-md bg-accent">
                                    ₱ {formData.fees.reduce((sum, fee) => sum + Number(fee.amount || 0), 0).toFixed(2)}
                                 </div>
                              </div>
                           </div>
                        </div>

                        {/* Signature section */}
                        <div className="grid grid-cols-2 gap-8 mt-8">
                           <div className={`text-center ${!isChiefRPS ? 'opacity-50' : ''}`}>
                              <div
                                 className={cn(
                                    "relative h-24 mb-4 border-2 border-dashed rounded-md flex items-center justify-center bg-accent/50 dark:bg-white",
                                    validationErrors.rpsSignature && "border-red-500"
                                 )}
                                 data-error={validationErrors.rpsSignature ? "true" : "false"}
                              >
                                 {formData.rpsSignature ? (
                                    <>
                                       <img
                                          src={formData.rpsSignature}
                                          alt="Chief RPS Signature"
                                          className="max-w-full max-h-full object-contain"
                                       />
                                       <Button
                                          variant="ghost"
                                          size="icon"
                                          className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-red-100 hover:bg-red-200"
                                          onClick={() => handleRemoveSignature('rpsSignature', rpsFileInputRef)}
                                          disabled={!isChiefRPS}
                                       >
                                          <X className="h-4 w-4 text-red-600" />
                                       </Button>
                                    </>
                                 ) : (
                                    <p className="text-muted-foreground">Chief RPS Signature</p>
                                 )}
                              </div>
                              {validationErrors.rpsSignature && (
                                 <div className="flex items-center mt-1 mb-2 text-sm text-red-500 justify-center">
                                    <AlertCircle className="h-4 w-4 mr-1" />
                                    {validationErrors.rpsSignature}
                                 </div>
                              )}
                              <p className="font-semibold text-foreground">SIMEON R. DIAZ</p>
                              <p className="text-xs text-muted-foreground">SVEMS/Chief, RPS</p>
                              <input
                                 type="file"
                                 ref={rpsFileInputRef}
                                 className="hidden"
                                 accept="image/*"
                                 onChange={(e) => handleSignatureUpload(e, 'rpsSignature')}
                                 disabled={!isChiefRPS}
                              />
                              <Button
                                 variant="outline"
                                 size="sm"
                                 className={cn(
                                    "mt-2",
                                    validationErrors.rpsSignature && isChiefRPS && "border-red-500 bg-red-50 dark:bg-red-950/10 hover:bg-red-100 dark:hover:bg-red-950/20"
                                 )}
                                 onClick={(e) => triggerFileInput(e, rpsFileInputRef)}
                                 disabled={!isChiefRPS}
                              >
                                 <UploadIcon className="h-4 w-4 mr-2" />
                                 Upload Signature
                              </Button>
                           </div>

                           <div className={`text-center ${!isChiefTSD ? 'opacity-50' : ''}`}>
                              <div
                                 className={cn(
                                    "relative h-24 mb-4 border-2 border-dashed rounded-md flex items-center justify-center bg-accent/50 dark:bg-white",
                                    validationErrors.tsdSignature && "border-red-500"
                                 )}
                                 data-error={validationErrors.tsdSignature ? "true" : "false"}
                              >
                                 {formData.tsdSignature ? (
                                    <>
                                       <img
                                          src={formData.tsdSignature}
                                          alt="TSD Signature"
                                          className="max-w-full max-h-full object-contain"
                                       />
                                       <Button
                                          variant="ghost"
                                          size="icon"
                                          className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-red-100 hover:bg-red-200"
                                          onClick={() => handleRemoveSignature('tsdSignature', tsdFileInputRef)}
                                          disabled={!isChiefTSD}
                                       >
                                          <X className="h-4 w-4 text-red-600" />
                                       </Button>
                                    </>
                                 ) : (
                                    <p className="text-muted-foreground">Chief TSD Signature</p>
                                 )}
                              </div>
                              {validationErrors.tsdSignature && (
                                 <div className="flex items-center mt-1 mb-2 text-sm text-red-500 justify-center">
                                    <AlertCircle className="h-4 w-4 mr-1" />
                                    {validationErrors.tsdSignature}
                                 </div>
                              )}
                              <p className="font-semibold text-foreground">Engr. CYNTHIA U. LOZANO</p>
                              <p className="text-xs text-muted-foreground">Chief, Technical Services Division</p>
                              <input
                                 type="file"
                                 ref={tsdFileInputRef}
                                 className="hidden"
                                 accept="image/*"
                                 onChange={(e) => handleSignatureUpload(e, 'tsdSignature')}
                                 disabled={!isChiefTSD}
                              />
                              <Button
                                 variant="outline"
                                 size="sm"
                                 className={cn(
                                    "mt-2",
                                    validationErrors.tsdSignature && isChiefTSD && "border-red-500 bg-red-50 dark:bg-red-950/10 hover:bg-red-100 dark:hover:bg-red-950/20"
                                 )}
                                 onClick={(e) => triggerFileInput(e, tsdFileInputRef)}
                                 disabled={!isChiefTSD}
                              >
                                 <UploadIcon className="h-4 w-4 mr-2" />
                                 Upload Signature
                              </Button>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>
            );
         default:
            return null;
      }
   };

   if (applicationsError) {
      return (
         <div className="p-4 text-destructive bg-destructive/10 rounded-md">
            Error loading applications: {applicationsError.message}
         </div>
      );
   }

   return (
      <div className="container mx-auto px-4 py-8 mt-14 max-w-5xl">
         <div className="bg-background border rounded-lg p-6 shadow-md">
            <div className="flex justify-between items-center mb-6">
               <Button variant="ghost" onClick={handleBack} className="flex items-center">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  <span className="text-foreground">Back to Dashboard</span>
               </Button>
               {/* <h2 className="text-2xl font-bold text-foreground">Create Order of Payment</h2> */}
            </div>
            <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
               {renderStep()}
               <div className="flex justify-between mt-6">
                  {step > 1 && (
                     <Button
                        type="button"
                        onClick={handlePrevious}
                        variant="outline"
                        disabled={isSubmitting}
                     >
                        Previous
                     </Button>
                  )}
                  {step < 2 ? (
                     <Button
                        type="button"
                        onClick={handleNext}
                        className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                        disabled={isSubmitting}
                     >
                        Next
                     </Button>
                  ) : (
                     <Button
                        type="button"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className={cn(
                           "relative bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white",
                           isSubmitting && "cursor-not-allowed opacity-70"
                        )}
                     >
                        {isSubmitting && (
                           <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        )}
                        {isSubmitting ? 'Creating...' : 'Create Order of Payment'}
                     </Button>
                  )}
               </div>
            </form>
         </div>
      </div>
   );
};

export default OrderOfPaymentForm;
